import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ReactQueryKeys } from 'constants/react-query-keys';
import { CareArea } from 'interfaces/care-area';
import CareAreaService from 'services/CareAreaService';
import { Facility } from 'interfaces/facility';
import FacilityService from 'services/FacilityService';
import { toast } from 'react-hot-toast';
import { parseErrorMessage } from 'helpers/parse-error-message';
import Spinner from 'components/UI/Spinner/Spinner';

/**
 * @description CareAreasProps interface defines the props for the CareAreas component.
 * @typedef {Object} CareAreasProps
 * @property {Facility} facility - The facility for which care areas are being managed
 * @property {boolean} canManage - Flag indicating if the user has permission to manage care areas
 */
type CareAreasProps = {
  facility: Facility;
  canManage: boolean;
};

/**
 * @description CareAreas component displays a list of care areas that can be added to a facility.
 * Shows all available care areas and indicates which ones are already added to the facility.
 *
 * @SADtrace SAD-003-4.1, SAD-003-4.2
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {CareAreasProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const CareAreas: FC<CareAreasProps> = ({ facility }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [addedCareAreaIds, setAddedCareAreaIds] = useState<
    Array<string | number>
  >([]);

  const { data: addedCareAreas, isLoading: isLoadingAddedCareAreas } = useQuery(
    {
      queryKey: [
        ReactQueryKeys.FACILITY_CARE_AREAS,
        { facilityId: facility.id },
      ],
      queryFn: () => FacilityService.getCareAreas(String(facility.id)),
      placeholderData: keepPreviousData,

      // @ts-ignore
      enabled: !!facility && !isNaN(facility.id),
    },
  );

  const { data: careAreas, isLoading: isLoadingCareAreas } = useQuery({
    queryKey: [ReactQueryKeys.CARE_AREAS],

    queryFn: () =>
      CareAreaService.get({
        pageIndex: 0,
        pageSize: 100,
        sorting: [
          {
            id: 'name',
            desc: false,
          },
        ],
      }),

    placeholderData: keepPreviousData,
  });

  /**
   * @description Updates the list of care area IDs that are already added to the facility
   * when the addedCareAreas data changes.
   */
  useEffect(() => {
    if (addedCareAreas?.data) {
      setAddedCareAreaIds(
        addedCareAreas.data.records.map((careArea) => Number(careArea.id)),
      );
    }
  }, [addedCareAreas]);

  /**
   * @description Handles the click event when a user selects a care area to add to the facility.
   * Makes an API call to associate the care area with the facility and updates the UI.
   *
   * @SADtrace SAD-003-4.1, SAD-003-4.2
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @async
   * @param {CareArea} careArea - The care area to be added to the facility
   *
   * @returns {Promise<void>}
   */
  const handleCareAreaClick = async (careArea: CareArea) => {
    try {
      await FacilityService.addCareArea(
        Number(facility.id),
        Number(careArea.id),
      );
      queryClient.invalidateQueries({
        queryKey: [ReactQueryKeys.FACILITY_CARE_AREAS],
      });
      toast.success(
        t('device.messages.facility__care_area__success', {
          careAreaName: careArea.name,
          facilityName: facility.name,
        }),
      );
    } catch (e: any) {
      let message = t('device.messages.facility__care_area__error');
      if (
        e?.response?.data?.statusCode === 400 ||
        e?.response?.data?.statusCode === 502
      ) {
        message = parseErrorMessage(e.response?.data);
      }
      toast.error(message);
    }
  };

  return (
    <>
      <h6
        className="device__care-area--title mt-3 pb-1"
        data-testid="careareas-component"
      >
        {t('device.messages.add_care_area', {
          name: facility.name,
        })}
      </h6>

      {isLoadingAddedCareAreas || isLoadingCareAreas ? (
        <Spinner isAbsolute />
      ) : null}

      {careAreas?.data?.records && careAreas.data.records.length === 0 && (
        <div className="alert mb-0">{t('messages.no_records')}</div>
      )}

      {careAreas?.data?.records && careAreas.data.records.length > 0 && (
        <div className="master-list device__care-area--full-list">
          {careAreas.data.records.map((careArea: CareArea) => {
            return (
              <a
                href="/#"
                onClick={(e) => {
                  e.preventDefault();
                  handleCareAreaClick(careArea);
                }}
                className={`master-list__item device-item ${
                  addedCareAreaIds.includes(Number(careArea.id))
                    ? 'item-disabled'
                    : ''
                }`}
                key={careArea.id}
              >
                {careArea.name}
              </a>
            );
          })}
        </div>
      )}
    </>
  );
};

export default CareAreas;
